import { useEffect, useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import { Wine, Activity, ShoppingBag, Search, Minus, Plus, X, ChevronDown, User } from "lucide-react";
import { getProducts, getActiveReservations, addConsumptionsBatch, fmt, fmtDate } from "@/lib/api";

const CATS = [
  { id: "bar", label: "Bar", icon: Wine },
  { id: "activities", label: "Activities", icon: Activity },
  { id: "shop", label: "Shop", icon: ShoppingBag },
];

export default function POS() {
  const [products, setProducts] = useState([]);
  const [activeRes, setActiveRes] = useState([]);
  const [selectedRes, setSelectedRes] = useState(null);
  const [tab, setTab] = useState("bar");
  const [cart, setCart] = useState({}); // {pid: {product, qty, price}}
  const [resSearch, setResSearch] = useState("");
  const [resOpen, setResOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [p, r] = await Promise.all([getProducts(), getActiveReservations()]);
    setProducts(p);
    setActiveRes(r);
  };

  useEffect(() => { load(); }, []);

  const filteredProducts = useMemo(
    () => products.filter(p => p.category === tab),
    [products, tab]
  );

  // Resolve price for a product given the currently-selected reservation's unit_type
  const priceFor = (p) => {
    const ut = selectedRes?.unit?.unit_type;
    if (ut && p.variant_prices && p.variant_prices[ut] != null) {
      return p.variant_prices[ut];
    }
    return p.price;
  };

  const filteredRes = useMemo(() => {
    if (!resSearch) return activeRes;
    const q = resSearch.toLowerCase();
    return activeRes.filter(r =>
      r.guest_name.toLowerCase().includes(q) ||
      (r.unit?.display_name || "").toLowerCase().includes(q)
    );
  }, [activeRes, resSearch]);

  const addToCart = (p) => {
    const resolvedPrice = priceFor(p);
    setCart(prev => {
      const cur = prev[p.id];
      return { ...prev, [p.id]: { product: p, qty: (cur?.qty || 0) + 1, price: cur?.price ?? resolvedPrice } };
    });
  };

  const decrement = (pid) => {
    setCart(prev => {
      const c = prev[pid];
      if (!c) return prev;
      if (c.qty <= 1) { const n = { ...prev }; delete n[pid]; return n; }
      return { ...prev, [pid]: { ...c, qty: c.qty - 1 } };
    });
  };

  const removeItem = (pid) => {
    setCart(prev => { const n = { ...prev }; delete n[pid]; return n; });
  };

  const changePrice = (pid, val) => {
    setCart(prev => ({ ...prev, [pid]: { ...prev[pid], price: parseFloat(val) || 0 } }));
  };

  const cartArr = Object.values(cart);
  const total = cartArr.reduce((s, c) => s + c.qty * c.price, 0);

  const handleAddToRoom = async () => {
    if (!selectedRes) return toast.error("Select a reservation first");
    if (cartArr.length === 0) return toast.error("Cart is empty");
    setSaving(true);
    try {
      await addConsumptionsBatch({
        reservation_id: selectedRes.id,
        items: cartArr.map(c => ({ product_id: c.product.id, quantity: c.qty, unit_price: c.price })),
      });
      toast.success(`Added ${cartArr.length} item(s) to ${selectedRes.guest_name}`);
      setCart({});
    } catch {
      toast.error("Failed to save order");
    } finally { setSaving(false); }
  };

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Top bar */}
      <div className="h-16 bg-white border-b border-slate-200 px-6 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <h1 className="font-heading text-xl font-bold text-slate-900">Bar POS</h1>
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            {activeRes.length} active guests
          </Badge>
        </div>

        <Popover open={resOpen} onOpenChange={setResOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-11 px-4 min-w-[320px] justify-between border-slate-300"
              data-testid="pos-reservation-select"
            >
              {selectedRes ? (
                <div className="flex items-center gap-2 min-w-0">
                  <User size={15} className="text-blue-600 shrink-0" />
                  <div className="text-left min-w-0">
                    <div className="font-semibold text-slate-900 truncate text-sm">{selectedRes.guest_name}</div>
                    <div className="text-xs text-slate-500 truncate">{selectedRes.unit?.display_name}</div>
                  </div>
                </div>
              ) : (
                <span className="text-slate-500 text-sm flex items-center gap-2"><User size={15} /> Select reservation...</span>
              )}
              <ChevronDown size={16} className="text-slate-400" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[380px] p-0" align="end">
            <div className="p-3 border-b border-slate-200">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search guest or room..."
                  value={resSearch}
                  onChange={(e) => setResSearch(e.target.value)}
                  className="pl-9 h-10"
                  autoFocus
                  data-testid="pos-reservation-search"
                />
              </div>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {filteredRes.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400">
                  {activeRes.length === 0 ? "No checked-in reservations" : "No match"}
                </div>
              ) : (
                filteredRes.map(r => (
                  <button
                    key={r.id}
                    onClick={() => { setSelectedRes(r); setResOpen(false); setResSearch(""); }}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
                    data-testid={`pos-res-option-${r.id}`}
                  >
                    <div className="font-semibold text-slate-900 text-sm">{r.guest_name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{r.unit?.display_name} · {fmtDate(r.check_in)} → {fmtDate(r.check_out)}</div>
                  </button>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Main split */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: products */}
        <div className="flex-1 overflow-hidden flex flex-col p-6">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-3 w-full h-12 mb-4 bg-white border border-slate-200">
              {CATS.map(c => {
                const Icon = c.icon;
                return (
                  <TabsTrigger
                    key={c.id}
                    value={c.id}
                    className="h-full data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                    data-testid={`pos-tab-${c.id}`}
                  >
                    <Icon size={15} className="mr-2" strokeWidth={1.75} /> {c.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>

          <div className="flex-1 overflow-y-auto pr-2">
            <div className="grid grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredProducts.map(p => {
                const inCart = cart[p.id];
                const isShop = p.category === "shop";
                const lowStock = isShop && p.stock <= 3;
                const outOfStock = isShop && p.stock <= 0;
                return (
                  <button
                    key={p.id}
                    onClick={() => addToCart(p)}
                    data-testid={`pos-product-${p.id}`}
                    className={`pos-btn relative h-28 bg-white rounded-lg border-2 p-4 flex flex-col justify-between text-left hover:border-blue-500 hover:bg-blue-50 ${inCart ? "border-blue-500 bg-blue-50 shadow-sm" : "border-slate-200"} ${outOfStock ? "opacity-75" : ""}`}
                  >
                    <div className="font-heading font-semibold text-base text-slate-900 leading-tight">
                      {p.name}
                      {p.variant_prices && Object.keys(p.variant_prices).length > 0 && (
                        <span className="ml-1 align-middle text-[9px] font-semibold text-blue-600 uppercase tracking-wider">· dyn</span>
                      )}
                    </div>
                    <div className="flex items-end justify-between">
                      <div className="flex flex-col gap-0.5">
                        <div className="text-sm font-medium text-slate-500" data-testid={`pos-price-${p.id}`}>{fmt(priceFor(p))}</div>
                        {isShop && (
                          <div
                            className={`text-[10px] font-semibold uppercase tracking-wider ${outOfStock ? "text-red-600" : lowStock ? "text-amber-600" : "text-slate-400"}`}
                            data-testid={`pos-stock-${p.id}`}
                          >
                            {outOfStock ? "Out of stock" : `${p.stock} in stock`}
                          </div>
                        )}
                      </div>
                      {inCart && (
                        <div className="h-7 min-w-[28px] px-2 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">
                          {inCart.qty}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
              {filteredProducts.length === 0 && (
                <div className="col-span-full text-sm text-slate-400 text-center py-12">
                  No products in this category
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: cart */}
        <aside className="w-[400px] bg-white border-l border-slate-200 flex flex-col shrink-0">
          <div className="p-5 border-b border-slate-200">
            <div className="text-xs uppercase tracking-wider text-slate-500">Current Order</div>
            {selectedRes ? (
              <>
                <div className="font-heading text-lg font-bold text-slate-900 mt-1 truncate">{selectedRes.guest_name}</div>
                <div className="text-xs text-slate-500 truncate">{selectedRes.unit?.display_name}</div>
              </>
            ) : (
              <div className="text-sm text-slate-400 mt-1">No guest selected</div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="pos-cart">
            {cartArr.length === 0 ? (
              <div className="text-center text-sm text-slate-400 py-16">
                Tap a product to start the order
              </div>
            ) : cartArr.map(c => (
              <div key={c.product.id} className="border border-slate-200 rounded-md p-3 bg-slate-50/40" data-testid={`pos-cart-item-${c.product.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 text-sm truncate">{c.product.name}</div>
                  </div>
                  <button onClick={() => removeItem(c.product.id)} className="text-slate-300 hover:text-red-500 p-0.5 shrink-0">
                    <X size={14} />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-2 gap-2">
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => decrement(c.product.id)} className="h-8 w-8 flex items-center justify-center border border-slate-300 rounded hover:bg-slate-100 active:scale-95">
                      <Minus size={14} />
                    </button>
                    <div className="w-8 text-center font-bold">{c.qty}</div>
                    <button onClick={() => addToCart(c.product)} className="h-8 w-8 flex items-center justify-center border border-slate-300 rounded hover:bg-slate-100 active:scale-95" data-testid={`pos-cart-inc-${c.product.id}`}>
                      <Plus size={14} />
                    </button>
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    value={c.price}
                    onChange={(e) => changePrice(c.product.id, e.target.value)}
                    className="h-8 w-20 text-right"
                  />
                  <div className="font-heading font-bold text-slate-900 w-16 text-right">{fmt(c.qty * c.price)}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-200 p-5 space-y-3 bg-slate-50/50">
            <div className="flex items-end justify-between">
              <div className="text-xs uppercase tracking-wider text-slate-500">Total</div>
              <div className="font-heading text-3xl font-bold text-slate-900" data-testid="pos-total">{fmt(total)}</div>
            </div>
            <Button
              disabled={!selectedRes || cartArr.length === 0 || saving}
              onClick={handleAddToRoom}
              className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white text-base font-bold"
              data-testid="pos-add-to-room-button"
            >
              Add to Room
            </Button>
            {!selectedRes && cartArr.length > 0 && (
              <div className="text-xs text-amber-600 text-center">Select a reservation to save this order</div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
